import React, { cloneElement, useId } from 'react'
import { Tooltip } from '@mui/material'

export interface DisabledReasonProps {
  /** Why the control is disabled. Undefined: the control is drawn as it is. */
  reason?: string
  /** One control (a Button, IconButton…) that takes `disabled`. */
  children: React.ReactElement
}

// Off screen but read by a screen reader (MUI's visuallyHidden).
const VISUALLY_HIDDEN: React.CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
}

/**
 * A control the user cannot use, shown disabled with the reason (Adam, 8 Oct:
 * never hide an action, say why and who to ask). With a reason it disables
 * the control, puts the reason in a tooltip on a span around it (a disabled
 * button fires no pointer events) and makes the reason the control's own
 * accessible description, so a screen reader reads it on the control.
 */
const DisabledReason: React.FC<DisabledReasonProps> = ({ reason, children }) => {
  const id = useId()
  if (!reason) return children
  return (
    <>
      <Tooltip describeChild title={reason}>
        <span style={{ display: 'inline-flex' }}>
          {cloneElement(children as React.ReactElement<Record<string, unknown>>, { disabled: true, 'aria-describedby': id })}
        </span>
      </Tooltip>
      <span id={id} style={VISUALLY_HIDDEN}>{reason}</span>
    </>
  )
}

export default DisabledReason
